import React, { useState, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import FHIR from 'fhirclient';
import FilePreviewer from 'react-file-previewer';
import moment from 'moment';
import { ReactComponent, Components } from 'react-formio';
import { humanFileSize } from '../../utils.js';
import { LoadingIcon } from '../LoadingIcon';
import { useFhirResolver, useFhirResources } from '../../hooks';
import { UploadSettingsContext, UploadSettingsProvider } from './UploadSettingsContext';
import './FhirFileComponent.scss';

class FileComponentWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            value: props.value
        };
    }

    render() {
        return (
            <UploadSettingsProvider>
                <FhirFileComponent
                    component={this.props.component} // These are the component settings if you want to use them to render the component.
                    value={this.state.value} // The starting value of the component.
                    onChange={this.props.onChange} // The onChange event to call when the value changes.
                />
            </UploadSettingsProvider>
        );
    }
}

const FhirFileComponent = (props) => {
    const mapping = useContext(UploadSettingsContext);
    const [value, setValue] = useState(props.value || []);
    const [selectedDR, setSelectedDR] = useState([]);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isFhirLoaded, setIsFhirLoaded] = useState(false);
    const [isLoadingDR, setIsLoadingDR] = useState(false);
    const [fhirClient, setFhirClient] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [files, setFiles] = useState([]);

    const { fhirResources } = useFhirResources(fhirClient);
    const { fhirResolveString } = useFhirResolver(() => fhirResources, [fhirResources]);

    useEffect(() => {
        document.body.classList.add('no-pdf');

        if (FHIR) {
            FHIR.oauth2.ready()
                .then((client) => {
                    setIsFhirLoaded(true);

                    const selected = [];
                    value.forEach((dr) => {
                        selected.push({ ...dr.documentReference });
                    });
                    setSelectedDR(selected);

                    if (client) {
                        setFhirClient(client);
                    }
                })
                .catch(() => { console.log('Error retrieving FHIR data.'); });
        }
    }, []);

    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    const getDocuments = (fhirClient) => {
        const query = props.component.patientQueryTemplate === false ? '' : (props.component.patientQueryTemplate || 'patientId=${patient.id}');
        setIsLoadingDR(true);
        return fhirClient.request(`DocumentReference?${fhirResolveString(query)}`);
    }

    const toggleModal = () => {
        if (!isUploadModalOpen) {
            getDocuments(fhirClient).then((data) => {
                if (data && data.total > 0) {
                    setFiles(data.entry);
                }

                setIsUploadModalOpen(true);
                setIsLoadingDR(false);
            });
        } else {
            setIsUploadModalOpen(!isUploadModalOpen);
        }
    }

    const setComponentValue = (data) => {
        setIsLoadingDR(true);
        setSelectedDR(data);
        getFiles(data)
            .then((payload) => {
                setValue(payload);
                setIsLoadingDR(false);
                setIsUploadModalOpen(false);
                props.onChange(payload, { noValidate: true, noCheck: true });
            });
    };

    const getFiles = async (documentReferences) => {
        const files = [];

        for (const documentReference of documentReferences) {
            const data = await getFile(documentReference);

            const file = {
                documentReference: { ...documentReference },
                ...data
            }

            files.push(file);
        }

        return files;
    }

    const getFile = async (item) => {
        const fileDr = await fhirClient.request(`DocumentReference/${mapping.getDocumentFileId(item)}`);
        const fileData = mapping.getDocumentData(fileDr);
        const fileUrl = mapping.getDocumentUrl(fileDr);
        const fileContentType = mapping.getDocumentContentType(fileDr);

        let fileType = '';

        if (fileContentType) {
            fileType = fileContentType.split('/')[1];
        }

        if (fileData) {
            return {
                fileType: fileType,
                storage: 'base64',
                type: fileContentType,
                name: mapping.getDocumentFilename(fileDr),
                originalName: mapping.getDocumentFilename(fileDr),
                url: `data:${fileContentType};base64,${fileData}`,
                size: mapping.getDocumentFileSize(fileDr)
            };
        } else {
            return getFileFromUrl(fileUrl);
        }
    };

    const getFileFromUrl = async (url) => {
        let mimeType = 'text/plain';
        let fileSize = 0;

        const file = await fhirClient.request(url)
            .then(result => {
                if (typeof result === 'string') {
                    const fileBase64 = btoa(result);
                    fileSize = (fileBase64.length * (3 / 4)) - 1;
                    return 'data:text/plain;base64,' + fileBase64;
                } else {
                    return result.blob()
                        .then(blob => new Promise(callback => {
                            mimeType = blob.type;
                            fileSize = blob.size;
                            const reader = new FileReader();
                            reader.onload = function () { callback(this.result) };
                            reader.readAsDataURL(blob);
                        }));
                }
            });

        return ({
            fileType: 'PDF',
            storage: 'base64',
            type: mimeType,
            name: url.split('/').pop(),
            originalName: url.split('/').pop(),
            url: file,
            size: fileSize
        });
    }

    const deleteFile = (id) => {
        const values = [...value];
        const valueIndex = values.findIndex(value => mapping.getDocumentFileId(value.documentReference) === id);
        values.splice(valueIndex, 1);

        const selectedReferences = [...selectedDR];
        const selectedDRIndex = selectedReferences.findIndex(dr => mapping.getDocumentFileId(dr) === id);
        selectedReferences.splice(selectedDRIndex, 1);

        setValue(values);
        setSelectedDR(selectedReferences);
        props.onChange(values, { noValidate: true, noCheck: true });
    }

    const openPreview = (e, data) => {
        e.preventDefault();
        e.stopPropagation();

        setIsPreviewOpen(true);
        setPreviewData(data);
    }

    const closePreview = () => {
        setIsPreviewOpen(false);
        setPreviewData(null);
    }

    if (isFhirLoaded) {
        return (
            <div className="file-upload">
                <SelectedFileContainer
                    files={value}
                    openModal={toggleModal}
                    openPreview={openPreview}
                    deleteFile={deleteFile}
                />

                {
                    isUploadModalOpen &&
                    <FileListingContainer
                        title={props.component.label}
                        uncategorizedLabel={props.component.uncategorizedLabel || 'Uncategorized'}
                        files={files}
                        selectedDR={selectedDR}
                        closeModal={toggleModal}
                        attachFiles={setComponentValue}
                        getFileData={getFile}
                        isLoading={isLoadingDR}
                        dateFormat={props.component.dateFormat}
                        searchOnEnter={props.component.searchOnEnter}
                    />
                }

                {
                    isPreviewOpen &&
                    <FilePreviewModal
                        item={previewData}
                        closePreview={closePreview}
                    />
                }
            </div>
        );
    } else {
        return (
            <div>FHIR data cannot be loaded.</div>
        );
    }
};

const SelectedFileContainer = (props) => {
    const mapping = useContext(UploadSettingsContext);

    return (
        <div className="selected-file-container">
            <ul className="list-group list-group-striped">
                <li className="list-group-item list-group-header hidden-xs hidden-sm">
                    <div className="row">
                        <div className="col-md-1"></div>
                        <div className="col-md-7"><strong>File Name</strong></div>
                        <div className="col-md-2"><strong>Size</strong></div>
                        <div className="col-md-2"><strong>Type</strong></div>
                    </div>
                </li>
                {
                    props.files?.length > 0 &&
                    props.files.map((item, index) =>
                        <li className="list-group-item" key={index}>
                            <div className="row">
                                <div className="col-md-1">
                                    <i className="fa fa-times" onClick={() => props.deleteFile(mapping.getDocumentFileId(item.documentReference))}></i>
                                </div>
                                <div className="col-md-7">
                                    <span className="list-group-item__filename">{item.name}</span>
                                    <a href="#" className="ml-3 small" onClick={(e) => { props.openPreview(e, item) }}>Preview</a>
                                </div>
                                <div className="col-md-2">{humanFileSize(item.size)}</div>
                                <div className="col-md-2">{item.type}</div>
                            </div>
                        </li>
                    )
                }
            </ul>
            <div className="fileSelector" onClick={props.openModal}>
                <i className="fa fa-cloud-upload"></i> Open and <span className="btn-link cursor-pointer">browse to attach files</span>
            </div>
        </div>
    );
};

const FilePreviewModal = (props) => {
    const isTextFile = props.item.type === 'text/plain';
    let encodedFile = props.item.url.split(',');
    encodedFile = encodedFile[encodedFile.length - 1];

    return (
        <div className="fhir-upload__overlay">
            <div className="fhir-upload__container p-3">
                <div className="fhir-upload__button-row mb-2">
                    <div className="fhir-upload__button w-100 btn btn-secondary" onClick={props.closePreview}>Close Preview</div>
                </div>
                <div className="fhir-upload__content">
                    {
                        isTextFile ?
                            <pre>{atob(encodedFile)}</pre>
                            :
                            <FilePreviewer
                                file={{
                                    data: encodedFile,
                                    mimeType: props.item.type,
                                    name: props.item.name
                                }}
                            />
                    }
                </div>
            </div>
        </div>
    );
};

const FileListingContainer = (props) => {
    const mapping = useContext(UploadSettingsContext);
    const [groupedFiles, setGroupedFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [clickedFile, setClickedFile] = useState(null);
    const [searchBy, setSearchBy] = useState('text');
    const [searchQuery, setSearchQuery] = useState(null);
    const [activeItem, setActiveItem] = useState(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    useEffect(() => {
        const files = [...props.files];

        files.sort(sortBy);

        setGroupedFiles(groupData(files));
    }, [props.files]);

    useEffect(() => {
        const selectedDR = [...props.selectedDR];

        selectedDR.forEach((item, index) => {
            const existingReference = findDocumentReferenceFromId(props.selectedDR, mapping.getDocumentFileId(item));
            const newReference = findDocumentReferenceFromId(props.files, mapping.getDocumentFileId(item));

            if (existingReference && newReference) {
                if (!hasSameChecksum(existingReference, newReference)) {
                    selectedDR[index].isUpdated = true;
                }
            }
        });

        setSelectedFiles(selectedDR);
    }, [props.selectedDR]);

    useEffect(() => {
        let data = [];

        if (searchQuery) {
            let matchedItems = [];

            if (searchBy === 'text') {
                const matchedItem = props.files.filter(item =>
                    (mapping.getDocumentUrl(item) + (mapping.getDocumentFilename(item) || '') + (mapping.getDocumentDescription(item) || ''))
                        .toLowerCase().includes(searchQuery));
                if (matchedItem.length > 0) {
                    matchedItems = matchedItems.concat(matchedItem);
                }
            } else if (searchBy === 'date') {
                matchedItems = matchedItems.concat(props.files.filter(item => moment(item.resource.created).isSame(searchQuery, 'day')));
			}

            data = groupData(matchedItems);
        } else {
            data = groupData(props.files);
        }

        setGroupedFiles(data);

    }, [searchQuery]);

    useEffect(() => {
        if (activeItem) {
            generatePreview(mapping.getDocumentFileId(activeItem));
        }
    }, [activeItem]);

    const sortBy = (a, b) => {
        //return a.resource.created < b.resource.created;
        return mapping.getDocumentSortKey(a) < mapping.getDocumentSortKey(b);
    };

    const groupData = (data) => {
        const groupedFiles = [];

        data.forEach(item => {
            const category = mapping.getDocumentCategory(item) || props.uncategorizedLabel;
            if (!groupedFiles[category]) {
                groupedFiles[category] = [];
            }

            groupedFiles[category].push(item);
        });

        return groupedFiles;
    }

    const itemClick = (id) => {
        let selected = [...selectedFiles];

        const selectedReference = findDocumentReferenceFromId(props.files, id);
        const index = selected.findIndex(item => mapping.getDocumentFileId(item) === id);

        if (index !== -1) {
            selected.splice(index, 1);
        } else {
            selected.push(selectedReference);
        }

        setSelectedFiles(selected);
    }

    const generatePreview = async (id) => {
        let selectedItem = null;

        setIsLoadingPreview(true);

        selectedItem = props.files.find(item => mapping.getDocumentFileId(item) === id);

        //const selectedFileUrl = mapping.getDocumentUrl(selectedItem);
        //const fileData = await props.getFileData(selectedFileUrl);
        const fileData = await props.getFileData(selectedItem);

        setClickedFile(fileData);
        setIsLoadingPreview(false);
    }

    const uploadClick = () => {
        props.attachFiles(selectedFiles);
    }

    const findDocumentReferenceFromId = (documentReferences, id) => {
        const selectedReference = documentReferences.find(item => mapping.getDocumentFileId(item) === id);

        return selectedReference;
    }

    const hasSameChecksum = (oldItem, newItem) => {
        const oldHash = mapping.getDocumentHash(oldItem);
        const newHash = mapping.getDocumentHash(newItem);

        return oldHash === newHash;
    }

    const updateSearchBy = (e) => {
        setSearchBy(e.target.value);
        clearQuery();
    }

    const searchClick = (e) => {
        if (!props.searchOnEnter || !e.which || e.which === 13) {
            const query = document.querySelector('.fhir-upload__searchbox').value.toLowerCase();
            setSearchQuery(query);
        }
    }

    const clearQuery = (e) => {
        let sb = document.querySelector('.fhir-upload__searchbox');
        sb.value = '';
        setSearchQuery('');
        sb.focus();
        if (e) {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
        }
        return false;
    }

    return (
        <div className="fhir-upload__overlay">
            <div className="fhir-upload__container d-flex flex-column">
                <LoadingIcon isLoading={props.isLoading} />
                <div className="fhir-upload__heading px-2 pt-3 pb-1 border-bottom">
                    <h6 className="font-weight-bold">{props.title}</h6>
                </div>
                <div className="fhir-upload__content flex-grow-1 overflow-hidden">
                    <div className="d-flex flow-row h-100">
                        <div className="w-50 flex-fill overflow-auto h-100">
                            <div className="fhir-upload__search form-inline border-bottom p-2 position-sticky">
                                <select className="fhir-upload__searchby form-control form-control-sm mr-1" onChange={updateSearchBy} >
                                    <option value="text">Name</option>
                                    <option value="date">Date</option>
                                </select>
                                <input className="fhir-upload__searchbox form-control form-control-sm w-50" type={searchBy} onKeyUp={searchClick} />
                                <button type="button" className="fhir-upload__button fhir-upload__searchbutton btn btn-sm btn-secondary py-1 px-2 mx-1" onClick={searchClick}>Search</button>
                                {searchQuery && <div className="bg-warning w-100 mt-2 p-1 bg-light small">Showing results containing "<strong>{searchQuery}</strong>". <span className="btn-link cursor-pointer" onClick={clearQuery}>Show everything</span></div>}
                            </div>
                            {
                                Object.entries(groupedFiles).length > 0 ?
                                    Object.entries(groupedFiles).map((items, index) =>
                                        <FileListingGroup
                                            key={index}
                                            title={items[0]}
                                            files={items[1]}
                                            selectedFiles={selectedFiles}
                                            activeItem={activeItem}
                                            onActive={setActiveItem}
                                            onClick={itemClick}
                                            dateFormat={props.dateFormat}
                                        />
                                    )
                                    : <div className="fhir-upload__empty-alert text-secondary small p-3">There are no files available to attach.</div>
                            }
                        </div>
                        <div className="fhir-upload__pane w-50 flex-fill border-left overflow-auto h-100 p-3">
                            <LoadingIcon isLoading={isLoadingPreview} />
                            {clickedFile && <FileListingPreview item={clickedFile} />}
                            {!clickedFile && <div className="text-secondary small">Select a file to preview</div>}
                        </div>
                    </div>
                </div>
                <div className="fhir-upload__button-row border-top p-3">
                    <button type="button" className="fhir-upload__button btn btn-primary btn-sm py-1 px-3 mr-1" onClick={uploadClick} disabled={selectedFiles.length === 0}>
                        {selectedFiles && selectedFiles.length > 1 ? `Attach ${selectedFiles.length} files` : 'Attach'}
                    </button>
                    <button type="button" className="fhir-upload__button btn btn-sm btn-secondary py-1 px-3 mr-1" onClick={props.closeModal}>Cancel</button>
                    <span className="text-secondary small mt-2 ml-2">Selecting {selectedFiles.length} out of {props.files.length} files. </span>
                    <div className="fhir-upload__item-detail d-flex small pull-right mt-2">
                        {activeItem && <>
                            {mapping.getDocumentLastModifiedDate(activeItem) && <span className="ml-2"><span className="text-muted">Date Modified: </span> {formatDate(mapping.getDocumentLastModifiedDate(activeItem), props.dateFormat)}</span>}
                            {mapping.getDocumentContentType(activeItem) && <span className="ml-2"><span className="text-muted">Content Type: </span> {mapping.getDocumentContentType(activeItem)}</span>}
                            {mapping.getDocumentFileSize(activeItem) && <span className="ml-2"><span className="text-muted">Size: </span> {humanFileSize(mapping.getDocumentFileSize(activeItem))}</span>}
                        </>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

const FileListingGroup = (props) => {
    const mapping = useContext(UploadSettingsContext);
    const [isGroupOpen, setIsGroupOpen] = useState(false);
    const cssClass = isGroupOpen ? 'fhir-upload__group open' : 'fhir-upload__group';

    const headingClick = () => {
        setIsGroupOpen(!isGroupOpen);
    }

    return (
        <div className={cssClass}>
            <div className="fhir-upload__group-heading p-2 border-bottom border-light font-weight-bold" onClick={headingClick}>
                {!isGroupOpen && <i className="fa fa-caret-right mr-1 d-inline-block text-center" aria-hidden="true"></i>}
                {isGroupOpen && <i className="fa fa-caret-down mr-1 d-inline-block text-center" aria-hidden="true"></i>}
                {props.title} <span className="small text-muted">({props.files.length})</span>
                {isGroupOpen && <span className="pull-right small mt-2 text-muted">Date created</span>}
            </div>
            {
                props.files.map((item, index) => {
                    const itemId = mapping.getDocumentFileId(item);
                    const activeItemId = mapping.getDocumentFileId(props.activeItem);

                    return (
                        <FileListingItem
                            key={index}
                            isActive={itemId === activeItemId}
                            item={item}
                            selected={props.selectedFiles.findIndex(selectedItem => mapping.getDocumentFileId(selectedItem) === itemId) !== -1}
                            isUpdated={props.selectedFiles.find(selectedItem => mapping.getDocumentFileId(selectedItem) === itemId)?.isUpdated}
                            onActive={props.onActive}
                            onClick={props.onClick}
                            dateFormat={props.dateFormat}
                        />
                    );
                })
            }
        </div>
    );
};

const FileListingItem = (props) => {
    const mapping = useContext(UploadSettingsContext);
    const documentFilename = mapping.getDocumentDisplayName(props.item);

    return (
        <div className={`fhir-upload__item border-bottom py-2 px-2 ${props.isActive && 'bg-light'}`} data-document-id={mapping.getDocumentFileId(props.item)} onClick={() => props.onActive(props.item)}>
            <input type="checkbox" className="ml-1 mr-2 align-middle" checked={props.selected} readOnly onClick={() => props.onClick(mapping.getDocumentFileId(props.item))} />
            <span className="fhir-upload__item-name">{documentFilename}</span>
            {props.isUpdated && <span className="badge badge-secondary bg-warning text-dark ml-1">Updated</span>}
            {mapping.getDocumentFileType(props.item) && <span className="badge badge-secondary ml-1">{mapping.getDocumentFileType(props.item)}</span>}
            <span className="fhir-upload__item-date small pull-right text-muted mt-1">{formatDate(mapping.getDocumentCreatedDate(props.item), props.dateFormat)}</span>
            {props.isActive && <div className="fhir-upload__item-description small text-muted ml-4 mt-1">{mapping.getDocumentDescription(props.item)}</div>}
        </div>
    );
};

const FileListingPreview = (props) => {
    const isTextFile = props.item.type === 'text/plain';
    let encodedFile = props.item.url.split(',');
    encodedFile = encodedFile[encodedFile.length - 1];

    return (
        <div className="fhir-upload__preview">
            <div className="fhir-upload__preview-container">
                {
                    isTextFile ?
                        <pre>{atob(encodedFile)}</pre>
                        :
                        <FilePreviewer
                            file={{
                                data: encodedFile,
                                mimeType: props.item.type,
                                name: props.item.name
                            }}
                        />
                }
            </div>
        </div>
    );
};

const formatDate = (date, format) => {
    return moment(date).format(format || 'YYYYMMDD');
}

class CustomFileComponent extends ReactComponent {
    constructor(component, options, data) {
        super(component, options, data);
    }

    /**
     * This function is the default settings for the component. At a minimum you want to set the type to the registered
     * type of your component (i.e. when you call Components.setComponent('type', MyComponent) these types should match.
     *
     * @param sources
     * @returns {*}
     */
    static schema() {
        return ReactComponent.schema({
            type: "FhirFileComponent",
            label: "Patient Files"
        });
    }

    /**
     * This function is called when the DIV has been rendered and added to the DOM. You can now instantiate the react component.
     *
     * @param DOMElement
     * #returns ReactInstance
     */
    attachReact(element) {
        let instance;

        return ReactDOM.render(
            <FileComponentWrapper
                component={this.component} // These are the component settings if you want to use them to render the component.
                value={this.dataValue} // The starting value of the component.
                onChange={this.updateValue} // The onChange event to call when the value changes.
                ref={(refer) => {
                    instance = refer;
                }}
            />,
            element,
            () => (this.reactInstance = instance)
        );
    }

    /**
     * Automatically detach any react components.
     *
     * @param element
     */
    detachReact(element) {
        if (element) {
            ReactDOM.unmountComponentAtNode(element);
        }
    }

    checkValidity(data, dirty, rowData) {
        return true;
    }

    validate(data, dirty, rowData) {
        return true;
    }
}

Components.addComponent('FhirFileComponent', CustomFileComponent);

export default CustomFileComponent;